import Cookie from 'universal-cookie';
import React from 'react';
import { Question, User } from '@/utils/types';
import { Exception } from '@microsoft/applicationinsights-web';
import { parseISO, format } from "date-fns";
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { feedbackScores } from "@/utils/feedbackBadges";
import { FeedbackType } from "@/utils/types";

export const getScore = (feedbackContext: FeedbackType) =>
  feedbackContext.reduce(
    (total, feed) => (total += feedbackScores[feed.badgeType]),
    0
  ) / feedbackContext.length;

export const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60); // Calculate minutes
  const seconds = time % 60; // Calculate remaining seconds
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0"
  )}`;
};

export const getLabel = (label: string) => {
  return label !== "" ? label
    .split(" ")
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" "): "";
};

export const genderOptions = [
  { value: "woman", label: "Woman" },
  { value: "man", label: "Man" },
  { value: "nonbinary", label: "Non-binary" },
  { value: "other", label: "Other" },
  { value: "preferNotToSay", label: "Prefer not to say" },
];

export const educationLevel = [
  { value: "under-graduate", label: "Under Graduate" },
  { value: "master-student", label: "Master's Student" },
  {
    value: "recent-graduate",
    label: "Recent Graduate (0-1 Years)",
  },
  {
    value: "bootcamp-student",
    label: "Bootcamp Student",
  },
  {
    value: "professional",
    label: "Professional",
  },
];

export const identityOptions = [
  { value: "first-generation", label: "First Generation" },
  { value: "international-student", label: "International Student" },
  { value: "non-native-english-speaker", label: "Non-Native English Speaker" },
];

export const raceOptions = [
  { value: "black", label: "Black or African American" },
  { value: "white", label: "White/Caucasian" },
  { value: "asian", label: "Asian" },
  { value: "latino", label: "Hispanic/Latino" },
  { value: "custom", label: "Custom" },
];

export const workExperienceOptions = [
  { value: "internship", label: "Internship" },
  { value: "apprenticeship", label: "Apprenticeship" },
  { value: "part-time", label: "Part time" },
  { value: "full-time", label: "Full time" },
];

export const roleOptions = [
  { value: "product management", label: "Product Management" },
  { value: "data scientist", label: "Data Scientist" },
  { value: "cyber security", label: "Cyber Security" },
  { value: "software engineering", label: "Software Engineering" },
  { value: "ui/ux design", label: "UI/UX Design" },
  {value:"web development", label:"Web Development"},
  {value: "business analyst", label:"Business Analyst",}
];

export const wantedRoles = [
  { value: "internship", label: "Internship" },
  { value: "apprenticeship", label: "Apprenticeship" },
  { value: "contract", label: "Contract" },
  { value: "part-time", label: "Part time" },
  { value: "full-time", label: "Full time" },
];

export const visaOptions = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

export const timeSpentOptions = [
  { value: "not-verify-often", label: "Not very often (<30 min)" },
  { value: "not-often", label: "Not often (1 hr)" },
  { value: "neutral", label: "Neutral (2-4 hrs)" },
  { value: "often", label: "Often (5-10 hrs)" },
  { value: "very-often", label: "Very often (10+ hrs)" },
];

export const dayOptions = [
  { value: "all", label: "All" },
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

export const dailyTimeOptions = [
  { value: "15-min", label: "15 minutes" },
  { value: "30-min", label: "30 minutes" },
  { value: "1-2-hrs", label: "1-2 hours" },
  { value: "2-4-hrs", label: "2-4 hours" },
  { value: "5-hrs", label: "5+ hours" },
];

export const discoveryOptions = [
  {
    value: "NSBE",
    label: "NSBE",
  },
  {
    value: "The Dev Difference campus workshop",
    label: "The Dev Difference campus workshop",
  },
  { value: "Google search", label: "Google search" },
  {
    value: "News article / online article",
    label: "News article / online article",
  },
  { value: "Social Media Ad", label: "Social Media Ad" },
  { value: "Friend / Word of Mouth", label: "Friend / Word of Mouth" },
  { value: "Career Fair", label: "Career Fair" },
  { value: "Other", label: "Other" },
];


export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const noFeedback = "I'm sorry, but you haven't provided a response to the interview question. Therefore, I cannot provide feedback on your answer. Please make sure to address the question and provide your thoughts."
export const specTypeToType:  {[key:string]:string}= {
  "coding":"technical",
  "software concepts":"technical",
  "system design":"technical",
  "product design":"technical",
  "pmhypothetical":"behavioral",
  "swehypothetical":"behavioral",
  "strategy": "technical",
  "estimation":"technical",
  "resume based":"behavioral",
  "general":"behavioral",
  "domain":"domain",
  "jobDescriptionQ":"jobDescriptionQ",
  "custom":"custom",
  "role":"role",
}

export const idsToTag:{[key:string]:string} ={
"pmhypothetical" :"behavioral",
"swehypothetical" :"behavioral",
"general": "essential"
}

export const createQuestion = (question: string, level: string|null, domain: string, type: string, specType: string, subCategories: string[]) : Question   =>{
  return {
    question, level, domain, type, specType, subCategories
  }
}
const primaryRoles: string[] = ["software engineering", "product management"];

/**
 * Returns the value of the specified cookie.
 * 
 * @param cookieName - The name of the cookie to retrieve.
 * @returns The value of the cookie, or null if the cookie is not found.
 */
export function getCookie(cookieName: string): string | null {
  const cookie = new Cookie();
  const token = cookie.get(cookieName);

  if (token === undefined) {
    return null;
  }
  return token;
}

/**
 * Checks if a question is in the selected questions array.
 * 
 * @param question - The question to check.
 * @param selected - The array of selected questions.
 * @returns True if the question is in the selected array, false otherwise.
 */
export const isQuestionInSelected = (
  question: Question,
  selected: Question[]
): boolean => {
  return selected.some((q:Question) => q.question == question.question);
};

/**
 * Determines if a role has multiple question packs based on its type.
 * 
 * @param role - The role to check.
 * @param type - The type of the question pack.
 * @returns True if the role has multiple question packs, false otherwise.
 */
export function hasMultipleQuestionPacks(role: string, type: string): boolean {
  return (type === "behavioral" || (type === "technical" && primaryRoles.includes(role)));
}

/**
 * Parses a query string into an object.
 * 
 * @param query - The query string to parse.
 * @returns An object representing the parsed query string.
 */
export function parseQuestionQuery(query: string): { [key: string]: string } {
  let sections = query.split("+");
  let parsed: { [key: string]: string } = {};
  for (let i = 0; i < sections.length; i++) {
    let item = sections[i].split("=");
    parsed[item[0]] = item[1];
  }
  return parsed;
}

export   const formatDateWithTime = (dateString: string) => {
  try {
    const parsedDate = new Date(dateString);
    return format(parsedDate, "MMM, dd, yyyy, hh:mm a");
  } catch (error) {
    console.error("Invalid date string:", error);
    return "";
  }
};

/**
 * Generates a pack name based on the provided parameters.
 *
 * @param {string} role - The role of the user.
 * @param {string} type - The type of the pack.
 * @param {string} specType - The specific type of the pack.
 * @param {string} domain - The domain of the pack.
 * @param {User} user - The user object containing user details.
 * @returns {string} - The generated pack name.
 *
 * The function follows these rules to generate the pack name:
 * - If the role is a primary role and the type is "technical", it returns the specType.
 * - If the role is not a primary role and the type is "technical", it returns the role.
 * - If the type is "behavioral", it returns:
 *   - "Essential 10" if specType is 'general'
 *   - The role if specType is 'role'
 *   - "Resume Based" otherwise
 * - If the type is "domain", it returns the domain.
 * - If the type is "org", it returns the capitalized organization name of the user.
 * - If the type is "jobDescriptionQ", it returns "Job Description".
 * - If the type is "custom", it returns "Custom Question".
 * - If none of the conditions match, it returns an empty string.
 */
export function getPackName(role: string, type: string, specType: string, domain: string, user: User): string {
  if (primaryRoles.includes(role) && type === "technical") {
    return (specType);
  }
  else if (!primaryRoles.includes(role) && type === "technical") {
    return (role);
  }
  else if (type === "behavioral") {
    return (specType === 'general' ? "Essential 10" : specType === 'role' ? role : "Resume Based")
  }
  else if (type === "domain") {
    return domain;
  }
  else if (type === "org") {
    return capitalizeFirstLetter(user.organization || '');
  }
  else if (type === "jobDescriptionQ") {
    return "Job Description";
  }
  else if (type === "custom") {
    return "Custom Question";
  }
  return '';
}

/**
 * Capitalizes the first letter of each word in a string.
 * 
 * @param word - The string to capitalize.
 * @returns The capitalized string.
 */
export function capitalizeFirstLetter(word: string): string {
  const l = word.split(' ');
  const newl = l.map(x => x.charAt(0).toUpperCase() + x.slice(1))
  return newl.join(' ');
}

/**
 * Checks if a user has a premium subscription.
 * 
 * @param user - The user to check.
 * @returns True if the user has a premium subscription, false otherwise.
 */
export function hasPremSub(user: User | null): boolean {
  // if (user && !user.isLegacyUser) {
  //   return user.hasActiveSubscription;
  // }
  return true;
}

/**
 * Checks if a user has a non-trial premium subscription.
 * 
 * @param user - The user to check.
 * @returns True if the user has a non-trial premium subscription, false otherwise.
 */
export function hasNonTrialPremSub(user: User | null): boolean {
  return true;
}

/**
 * Retrieves the friendly organization names from a user's subscription.
 * 
 * @param user - The user to retrieve the organization names from.
 * @returns The friendly organization names.
 */
export const GetFriendlyOrgNames = (user:User|null) : string => {
  if (user){
      const sub = user["subscription"];
      if (sub)
      {
          const elements = sub.split(";;=");
          if(elements.length === 4){
              return elements[1]
          }
      }
  }
  return "";
}

/**
 * Generates a timestamp string representing the current date and time.
 * 
 * @returns The generated timestamp string.
 */
export function getTimestamp(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  
export async function feedbackMap(feedback: string, canCallGpt?: boolean): Promise<[string[], string[], string[]] | null> {
  let feedA: any[] 
  try {
    let newf: any = await JSON.parse(feedback);
    feedA = newf["feedback"];
    if(feedA== undefined)
    {
      throw new Error("Feedback array is undefined");
    }
  }
  catch (e) {
    //fall back for old feedback that wasn't structured gpt output
    try{
      let newf: string = feedback.replace(/\\"/g, '\"').replace(/\\n/g, '   ').replace(/^\"/, '').replace(/\"$/, '').replace(/\\/g, '');
      if (newf.startsWith("```json")) {
          newf = newf.substring(7, newf.length - 4);
      }
      feedA = await JSON.parse(newf);
    }
    catch (e) {
      console.log("Error in feedbackMap:", e);
      return null;
    }
  }

    let feedbackGrade: string[] = [];
    let feedbackCriteria: string[] = [];
    let criteriaCont: string[] = [];

    const mapping: { [key: string]: string } = {
        "Poor": "Poor",
        "Satisfactory": "Satisfactory",
        "Fair": "Satisfactory",
        "Good": "Good",
        "Excellent": "Exceptional"
    };
    const kudos: { [key: string]: string } = {
        "Poor": "<br/><br/>You tried your best, but there are some things you need to work on. Keep practicing!",
        "Good": "<br/><br/>Good answer! With a bit more practice, your answers will be exceptional.",
        "Fair": "<br/><br/>Not bad! With some more practice, your answers will be exceptional.",
        "Satisfactory": "<br/><br/>Not bad! With some more practice, your answers will be exceptional.",
        "Excellent": "<br/><br/>Well done! This is an answer a skilled candidate would give."
    };

    //validate feedback grade and details
    for (let i = 0; i < feedA.length; i++) {
        let grade: string = feedA[i]["Grade"];
        let content: string = feedA[i]["Details"];
        if (!(grade in mapping)) {
          feedA[i]["Grade"] = "Poor";
        }
        if(!content){
          feedA[i]["Details"] = "No evaluation available.";
        }
    }
    for (let i = 0; i < feedA.length; i++) {
        feedbackCriteria.push(feedA[i]["Criteria"]);
        let grade: string = feedA[i]["Grade"];
        let cont: string = feedA[i]["Details"].replace(/\"/g, '').replace(/   /g, '<br/>').replace(/\\/g, '');
        feedbackGrade.push(grade in mapping ? mapping[grade] : "Poor");
        criteriaCont.push(grade in kudos ? cont + kudos[grade] : cont + kudos["Poor"]);
    }
    return [feedbackCriteria, feedbackGrade, criteriaCont];
}

/**
 * Parses the feedback string and extracts feedback criteria, grades, and detailed evaluations.
 * 
 * @param feedback - The feedback string to be parsed.
 * @returns A tuple containing three arrays: feedback criteria, feedback grades, and detailed evaluations.
 *          Returns null if no valid feedback is found.
 * 
 * The function follows these steps:
 * 1. Extracts feedback content based on numbered patterns.
 * 2. Maps grades to predefined categories.
 * 3. Adds kudos messages based on the grade.
 * 4. Returns the extracted feedback criteria, grades, and detailed evaluations.
 * 
 * The feedback string is expected to have the following format:
 * - Numbered sections (e.g., "1. ...<br/>2. ...").
 * - Grades and detailed evaluations within each section.
 * - Optional kudos messages based on the grade.
 * 
 * Example feedback string:
 * ```
 * 1. **Criteria 1**<br/>Grade: Good<br/>Detailed Evaluation: ...<br/>2. ...
 * ```
 */
/**
 * Maps feedback string to an array of feedback criteria, grades, and detailed evaluations.
 * 
 * @param feedback - The feedback string to be parsed.
 * @returns A tuple containing three arrays: feedback criteria, feedback grades, and detailed evaluations.
 *          Returns null if no feedback criteria are found.
 */
export function feedbackMap_OG(feedback: string): [string[], string[], string[]] | null {
    let contentArr: string[] = [];
    let ind: number = 1;
    let match0: RegExpMatchArray | null;
    let toMatch: string = feedback;
    while(ind > 0){
        let pattern: RegExp = new RegExp(`${ind}\\.(.*?)<br/>${ind+1}`);
        match0 = toMatch.match(pattern);
        if (match0 && match0[1]) {
            contentArr.push(match0[1]);
        }
        else{
            pattern = new RegExp(`${ind}\\)(.*?)<br/>${ind+1}`);
            match0 = toMatch.match(pattern);
            if (match0 && match0[1]) {
                contentArr.push(match0[1]);
            }
            else{
                break;
            }
        }
        ind++;
    }
    if(ind>1)
    {
        let p: RegExp = new RegExp(`<br/>${ind}\\.(.*?)$`);
        match0 = feedback.match(p);
        if (match0 && match0[1]) {
            let i: number = match0[1].lastIndexOf("<br/><br/>Overall,");
            contentArr.push(i !== -1 ? match0[1].slice(0,i): match0[1]);
        }
    }

    if (ind === 1){
        let pattern: RegExp = /1\.(.*?)$/;
        match0 = feedback.match(pattern);
        if (match0 && match0[1]) {
            let i: number = match0[1].lastIndexOf("<br/><br/>Overall,");
            contentArr.push(i !== -1 ? match0[1].slice(0,i): match0[1]);
        }
    }

    if(contentArr.length === 0){
        return null;
    }

    let feedbackGrade: string[] = [];
    let feedbackCriteria: string[] = [];
    let criteriaCont: string[] = [];
    const mapping: {[key: string]: string} = {
        "Poor": "Poor",
        "Satisfactory": "Satisfactory",
        "Fair":"Satisfactory",
        "Good":"Good",
        "Excellent" : "Exceptional"
    };
    const kudos: {[key: string]: string} = {
        "Poor": " <br/><br/>You tried your best, but there are some things you need to work on. Keep practicing!",
        "Good":"<br/><br/>Good answer! With a bit more practice, your answers will be exceptional.",
        "Fair": "<br/><br/>Not bad! With some more practice, your answers will be exceptional.",
        "Satisfactory": "<br/><br/>Not bad! With some more practice, your answers will be exceptional.",
        "Excellent" : " <br/><br/>Well done! This is an answer a skilled candidate would give."
    };
    contentArr.forEach((text: string) => {
        if(!/^\s*$/.test(text))
        {
            //extract heading
            let pattern: RegExp = /\*\*(.*?)\*\*/;

            const match: RegExpMatchArray | null = text.match(pattern);

            if (match && match[1]) {
                let cont: string = "";
                let grade: string = "";
                let heading: string = match[1];
                pattern = /Grade: (.*?)\<br\/\>/;

                const match2: RegExpMatchArray | null = text.match(pattern);
                
                if (match2 && match2[1]) {
                    grade = match2[1];
                }
                pattern = /Detailed Evaluation:(.*)/;
                const match3: RegExpMatchArray | null = text.match(pattern);
                if (match3 && match3[1]) {
                    cont = match3[1];
                    cont = cont[0] === '<' ? cont.slice(5) : cont;
                }

                if(cont!=="")
                {
                    feedbackCriteria.push(heading);
                    feedbackGrade.push(grade in mapping ? mapping[grade] : "Poor");
                    criteriaCont.push(grade in kudos ?  cont + kudos[grade] : cont + kudos["Poor"]);
                }
                else if(grade.split(' ').length > 3)
                {
                    feedbackCriteria.push(heading);
                    feedbackGrade.push("Poor");
                    criteriaCont.push(grade);
                }
                else if(grade!== ""){
                    pattern = new RegExp(`Grade: ${grade}<br/>(.*?)$`);
                    const match4: RegExpMatchArray | null = text.match(pattern);
                    if (match4 && match4[1]) {
                        cont = match4[1];
                        cont = cont[0] === '<' ? cont.slice(5) : cont;
                        feedbackCriteria.push(heading);
                        feedbackGrade.push(grade in mapping ? mapping[grade] : "Poor");
                        criteriaCont.push(grade in kudos ?  cont + kudos[grade] :  cont + kudos["Poor"]);
                    }
                }
            }
        }
    });
    return feedbackCriteria.length === 0 ? null : [feedbackCriteria, feedbackGrade, criteriaCont];
}

// Generates a globally unique identifier (GUID).
export function generateGUID(): string {
  const randomBytes = new Uint8Array(16);
  crypto.getRandomValues(randomBytes);

  // Set the version and variant bits according to RFC 4122.
  randomBytes[6] = (randomBytes[6] & 0x0f) | 0x40;
  randomBytes[8] = (randomBytes[8] & 0x3f) | 0x80;

  let uuid = '';
  for (let i = 0; i < 16; i++) {
    uuid += (randomBytes[i] + 0x100).toString(16).substr(1);
    if ([3, 5, 7, 9].includes(i)) {
      uuid += '-';
    }
  }

  return uuid;
}

// Converts a question string into a JSX element array, optionally appending a level tag.
export function GetQuestionDisplay(question: string, qLevel: string | null = null): JSX.Element[] {
  try {
    let contentArr = question.split('<br/>');
    let questionFrags = contentArr.map((item, index) => {
      if (!/^\s*$/.test(item)) {
        return (
          <React.Fragment key={generateGUID()}>
            {index === (contentArr.length - 1) && qLevel !== null && <>{item} <span className="levelTag"><span id={qLevel} /><b className="pl-2">{qLevel}</b></span></>}
            {(index !== contentArr.length - 1 || qLevel === null) && item}
            {index !== contentArr.length - 1 && <br />}
          </React.Fragment>
        );
      } else {
        return (<React.Fragment key={generateGUID()}></React.Fragment>)
      }
    });
    return questionFrags
  }
  catch {
    return [];
  }
}

// Determines if a user is an admin based on their subscription details.
export function isAdmin(user: User | null) {
  if (user) {
    const sub = user["subscription"];
    if (sub) {
      const elements = sub.split(";;=");
      if (elements.length === 4) {
        return elements[3] === "admin";
      }
    }
  }
  return false;
}
